/**
 * Geocoding helpers — reverse geocoding via a server-side Firebase Callable
 * (reverseGeocodeLatLng) backed by Google Geocoding API. The API key lives
 * in Secret Manager and never touches the browser.
 *
 * Usage policy: call reverseGeocode only once per coordinate input (on blur
 * or submit), never on every keystroke.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const _reverseGeocode = httpsCallable<{ lat: number; lng: number }, { address: string | null }>(
  functions,
  'reverseGeocodeLatLng',
);

/** Reverse-geocode {lat, lng} → human-readable address string via Cloud Function. */
export async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const result = await _reverseGeocode({ lat, lng });
    return result.data.address;
  } catch {
    return null;
  }
}

/** A parsed coordinate pair (decimal degrees). */
export interface ParsedCoords {
  lat: number;
  lng: number;
}

/**
 * PURE — parse a free-typed coordinate string into {lat, lng} decimal degrees.
 *
 * Handles:
 *   · Decimal pair: "21.2345, 81.6789" or "-33.8688 151.2093"
 *   · DMS pair:     `21°14'4.44"N, 81°40'44.04"E`
 *                   `21 14 4.44 N, 81 40 44.04 E`
 *   · Google Maps URL: "@21.2345,81.6789" or "?q=21.2345,81.6789"
 *
 * Returns null when unparseable or out of range.
 */
export function parseCoordsString(raw: string): ParsedCoords | null {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return null;

  // ── Try extracting from Google Maps URL first ──────────────────────────
  // @lat,lng (view centre)
  const atMatch = trimmed.match(/@(-?\d+\.?\d+),(-?\d+\.?\d+)/);
  if (atMatch) {
    const lat = parseFloat(atMatch[1]);
    const lng = parseFloat(atMatch[2]);
    if (inRange(lat, lng)) return { lat, lng };
  }
  // ?q= / query= / ll= / daddr= style
  const qMatch = trimmed.match(/[?&](?:q|query|ll|daddr)=(-?\d+\.?\d+)[,+](-?\d+\.?\d+)/);
  if (qMatch) {
    const lat = parseFloat(qMatch[1]);
    const lng = parseFloat(qMatch[2]);
    if (inRange(lat, lng)) return { lat, lng };
  }
  // !3d<lat>!4d<lng> (pin form)
  const pinMatch = trimmed.match(/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/);
  if (pinMatch) {
    const lat = parseFloat(pinMatch[1]);
    const lng = parseFloat(pinMatch[2]);
    if (inRange(lat, lng)) return { lat, lng };
  }

  // ── Try DMS pair ───────────────────────────────────────────────────────
  // e.g. "21°14'4.44"N, 81°40'44.04"E" or "21 14 4.44 N, 81 40 44.04 E"
  const dmsRe =
    /(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([NSns])[,\s]+(\d+)[°\s]+(\d+)['\s]+(\d+\.?\d*)["\s]*([EWew])/;
  const dmsMatch = trimmed.match(dmsRe);
  if (dmsMatch) {
    const [, nd, nm, ns, ndir, ed, em, es, edir] = dmsMatch;
    const lat =
      (parseFloat(nd) + parseFloat(nm) / 60 + parseFloat(ns) / 3600) *
      (['S', 's'].includes(ndir) ? -1 : 1);
    const lng =
      (parseFloat(ed) + parseFloat(em) / 60 + parseFloat(es) / 3600) *
      (['W', 'w'].includes(edir) ? -1 : 1);
    if (inRange(lat, lng)) return { lat, lng };
  }

  // ── Try decimal pair ───────────────────────────────────────────────────
  // "21.2345, 81.6789" or "21.2345 81.6789"
  const decimalMatch = trimmed.match(/^(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)$/);
  if (decimalMatch) {
    const lat = parseFloat(decimalMatch[1]);
    const lng = parseFloat(decimalMatch[2]);
    if (inRange(lat, lng)) return { lat, lng };
  }

  return null;
}

function inRange(lat: number, lng: number): boolean {
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}
